import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Calculator } from 'lucide-react';
import { LoanSlider } from './emi/LoanSlider';
import { EMIResult } from './emi/EMIResult';
import {
  calculateEMI,
  formatINR,
  DEFAULT_LOAN_AMOUNT,
  MIN_LOAN_AMOUNT,
  MAX_LOAN_AMOUNT,
  LOAN_AMOUNT_STEP,
  DEFAULT_INTEREST_RATE,
  MIN_INTEREST_RATE,
  MAX_INTEREST_RATE,
  INTEREST_RATE_STEP,
  DEFAULT_TENURE_YEARS,
  MIN_TENURE_YEARS,
  MAX_TENURE_YEARS,
  TENURE_STEP_YEARS,
} from '../lib/emi';

export const EMICalculator = () => {
  const [amount, setAmount] = useState(DEFAULT_LOAN_AMOUNT);
  const [rate, setRate] = useState(DEFAULT_INTEREST_RATE);
  const [tenure, setTenure] = useState(DEFAULT_TENURE_YEARS);

  const { emi, totalPayment, totalInterest, principalPct } = calculateEMI(amount, rate, tenure);

  const trackApply = () => {
    if (window.gtag) {
      window.gtag('event', 'emi_calculator_apply', {
        event_category: 'EMI Calculator',
        event_label: `Amount: ${amount}, Rate: ${rate}, Tenure: ${tenure}`,
      });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-cyan-950" data-testid="emi-calculator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-900/50 text-cyan-300 rounded-full text-sm font-medium mb-4">
            <Calculator className="w-4 h-4" />
            EMI Calculator
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Plan Your Loan in Seconds
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Adjust the sliders to instantly estimate your monthly EMI
          </p>
        </div>

        <Card className="border-0 shadow-2xl max-w-5xl mx-auto overflow-hidden">
          <CardContent className="p-0">
            <div className="grid md:grid-cols-2">
              <div className="p-8 space-y-8 bg-white flex flex-col justify-center">
                <LoanSlider
                  label="Loan Amount"
                  displayValue={formatINR(amount)}
                  value={amount}
                  onChange={setAmount}
                  min={MIN_LOAN_AMOUNT}
                  max={MAX_LOAN_AMOUNT}
                  step={LOAN_AMOUNT_STEP}
                  minLabel="₹1 Lakh"
                  maxLabel="₹5 Crore"
                  testIdPrefix="emi-amount"
                />
                <LoanSlider
                  label="Interest Rate (p.a.)"
                  displayValue={`${rate}%`}
                  value={rate}
                  onChange={setRate}
                  min={MIN_INTEREST_RATE}
                  max={MAX_INTEREST_RATE}
                  step={INTEREST_RATE_STEP}
                  minLabel={`${MIN_INTEREST_RATE}%`}
                  maxLabel={`${MAX_INTEREST_RATE}%`}
                  testIdPrefix="emi-rate"
                />
                <LoanSlider
                  label="Loan Tenure"
                  displayValue={`${tenure} ${tenure === 1 ? 'Year' : 'Years'}`}
                  value={tenure}
                  onChange={setTenure}
                  min={MIN_TENURE_YEARS}
                  max={MAX_TENURE_YEARS}
                  step={TENURE_STEP_YEARS}
                  minLabel="1 Year"
                  maxLabel={`${MAX_TENURE_YEARS} Years`}
                  testIdPrefix="emi-tenure"
                />
              </div>

              <EMIResult
                amount={amount}
                emi={emi}
                totalInterest={totalInterest}
                totalPayment={totalPayment}
                principalPct={principalPct}
                onApply={trackApply}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default EMICalculator;
